import { ReactNode } from "react";

// Props for Table
interface TableProps {
  children: ReactNode; // Table content (thead, tbody, etc.)
  className?: string; // Optional className for styling
  padded?: boolean; // Pad every cell (see below)
}

// Props for TableHeader
interface TableHeaderProps {
  children: ReactNode; // Header row(s)
  className?: string; // Optional className for styling
}

// Props for TableBody
interface TableBodyProps {
  children: ReactNode; // Body row(s)
  className?: string; // Optional className for styling
}

// Props for TableRow
interface TableRowProps {
  children: ReactNode; // Cells (th or td)
  className?: string; // Optional className for styling
}

// Props for TableCell
interface TableCellProps {
  children: ReactNode; // Cell content
  isHeader?: boolean; // If true, renders as <th>, otherwise <td>
  className?: string; // Optional className for styling
}

// Table Component
//
// `whitespace-nowrap` is the DEFAULT, and it is the whole reason this table
// behaves at a narrow window. An HTML table shrinks to fit by BREAKING TEXT
// before it agrees to grow, so a cell that is allowed to wrap will wrap: a
// sweep of every table screen at 1200px found nine of them doing it, worst at
// five lines, with dates coming out as "2026-\n09-\n25". The `overflow-x-auto`
// wrapper these all sit in never got anything to scroll, because the table
// never exceeded it.
//
// Nearly every cell in this app holds ONE VALUE — a date, a quantity, a
// rupee amount, a badge — and one value belongs on one line. A cell that
// genuinely holds prose opts out with `whitespace-normal`, or truncates
// inside a `max-w-*`, which is the better answer for a column that would
// otherwise set the table's width on its own.
//
// `padded` pads every cell, for screens whose cells carry no padding of their
// own — without it the rows touch the card's edge (live walk 1, F9). Compact
// sideways (px-3) so a nine-column ledger still fits a 1440px window, with the
// first column inset from the card edge. A cell's own padding class loses to
// it, so don't mix the two on one table.
const PADDED =
  "[&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5 [&_td:first-child]:pl-5 [&_th:first-child]:pl-5";

const Table: React.FC<TableProps> = ({ children, className, padded = false }) => {
  return (
    <table className={`min-w-full whitespace-nowrap ${padded ? PADDED : ""} ${className ?? ""}`}>
      {children}
    </table>
  );
};

// TableHeader Component
const TableHeader: React.FC<TableHeaderProps> = ({ children, className }) => {
  return <thead className={className}>{children}</thead>;
};

// TableBody Component
const TableBody: React.FC<TableBodyProps> = ({ children, className }) => {
  return <tbody className={className}>{children}</tbody>;
};

// TableRow Component
const TableRow: React.FC<TableRowProps> = ({ children, className }) => {
  return <tr className={className}>{children}</tr>;
};

// TableCell Component
const TableCell: React.FC<TableCellProps> = ({
  children,
  isHeader = false,
  className,
}) => {
  const CellTag = isHeader ? "th" : "td";
  return <CellTag className={` ${className}`}>{children}</CellTag>;
};

export { Table, TableHeader, TableBody, TableRow, TableCell };
